<template>
    <ManagementListPage title="Certidão de habilitação"
        subtitle="Solicitações enviadas por credores, procuradores e devedora."
        :result-text="`${dataList.count} solicitações encontradas`" :breadcrumbs="breadcrumbs"
        :summary-cards="counters" page-class="certificate-claim-list"
        @summary-click="toggleStatus($event.status)">
        <template #actions>
            <v-btn outlined color="primary" elevation="0" :disabled="!nextPending" @click="viewItem(nextPending)">
                <v-icon left small>mdi-clipboard-check-outline</v-icon>Analisar próxima pendente
            </v-btn>
            <v-btn text color="primary" @click="exportCsv">
                <v-icon left small>mdi-download</v-icon>Exportar CSV
            </v-btn>
        </template>
        <template #filters>
            <div class="op-filter-toolbar">
                <v-text-field v-model="dataList.filter.term" dense outlined hide-details clearable
                    prepend-inner-icon="mdi-magnify" placeholder="Buscar por nome, CPF/CNPJ ou processo"
                    @keyup.enter="applyFilters" />
                <v-btn outlined color="primary" @click="showAdvanced = !showAdvanced">
                    <v-icon left small>mdi-tune-variant</v-icon>Filtros
                    <span v-if="advancedFilterCount" class="management-filter-count">{{ advancedFilterCount }}</span>
                    <v-icon right small>{{ showAdvanced ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
                <v-btn color="primary" elevation="0" @click="applyFilters">
                    <v-icon left small>mdi-magnify</v-icon>Buscar
                </v-btn>
            </div>
            <v-expand-transition>
                <div v-if="showAdvanced" class="op-filter-panel mt-3 pt-3">
                    <div class="op-filter-panel-head">
                        <div><strong>Filtros avançados</strong><span>Refine as solicitações exibidas na listagem.</span></div>
                    </div>
                    <v-row dense class="op-filter-grid management-advanced-filters">
                        <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Protocolo</label><v-text-field v-model="dataList.filter.protocol" class="certificate-protocol-field" dense outlined hide-details clearable prepend-inner-icon="mdi-pound" placeholder="CHC-XXXXXXXX" /></div></v-col>
                        <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Status</label><v-select v-model="dataList.filter.status" :items="getFilterStatusTypes()" item-text="name" item-value="id" dense outlined hide-details placeholder="Todos os status" /></div></v-col>
                        <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Caso</label><v-autocomplete v-model="dataList.filter.client_id" :items="dataset.clients" item-value="id" item-text="name" dense outlined hide-details placeholder="Todos os casos" /></div></v-col>
                        <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Requerente</label><v-select v-model="dataList.filter.requester_type" :items="requesterTypes" item-text="name" item-value="id" dense outlined hide-details placeholder="Todos os requerentes" /></div></v-col>
                        <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Ramo da Justiça</label><v-select v-model="dataList.filter.justice_branch" :items="getFilterJusticeBranchTypes()" item-text="name" item-value="id" dense outlined hide-details placeholder="Todos os ramos" /></div></v-col>
                        <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Origem do crédito</label><v-select v-model="dataList.filter.credit_origin" :items="getFilterCreditOriginTypes()" item-text="name" item-value="id" dense outlined hide-details placeholder="Todas as origens" /></div></v-col>
                        <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Classe informada</label><v-select v-model="dataList.filter.class_type" :items="getFilterCreditorTypes()" item-text="name" item-value="id" dense outlined hide-details placeholder="Todas as classes" /></div></v-col>
                        <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Classe revisada</label><v-select v-model="dataList.filter.reviewed_class_type" :items="getFilterCreditorTypes()" item-text="name" item-value="id" dense outlined hide-details placeholder="Todas as classes" /></div></v-col>
                    </v-row>
                </div>
            </v-expand-transition>
            <div v-if="showAdvanced" class="op-filter-footer certificate-filter-actions">
                <span>{{ advancedFilterCount ? `${advancedFilterCount} filtro(s) ativo(s)` : 'Nenhum filtro adicional aplicado' }}</span>
                <v-btn v-if="advancedFilterCount" text small color="primary" @click="clearAdvancedFilters">Limpar filtros</v-btn>
            </div>
            <div v-if="activeFilters.length" class="chc-queue__active-filters">
                <small>Filtros ativos</small>
                <v-chip v-for="filter in activeFilters" :key="filter.key" small close
                    @click:close="removeFilter(filter.key)">{{ filter.label }}</v-chip>
                <button type="button" @click="clearFilters">Limpar tudo</button>
            </div>
        </template>
        <template #table>
            <v-data-table :headers="dataList.headers" :items="dataList.data"
                :server-items-length="dataList.count" :loading="dataList.loading"
                :options.sync="dataList.options" :footer-props="dataList.footerOptions"
                :disable-sort="true" class="app-table management-list-table management-minimal-table chc-queue__table"
                no-data-text="Nenhuma solicitação encontrada" loading-text="Carregando solicitações..."
                @update:items-per-page="pageSizeChanged" @update:page="pageChanged">
                <template v-slot:[`item.protocol`]="{ item }"><strong class="op-cell-primary">{{ item.protocol || '-' }}</strong></template>
                <template v-slot:[`item.requester`]="{ item }">
                    <strong class="op-cell-primary">{{ item.requester_name || creditorName(item) }}</strong>
                    <small class="op-cell-secondary">{{ formatCpfCnpj(item.requester_registration || creditorRegistration(item)) }}</small>
                </template>
                <template v-slot:[`item.requester_type`]="{ item }">
                    <v-chip x-small label class="op-table-badge" :color="getRequesterTypeColor(item.requester_type)">{{ getRequesterTypeLabel(item.requester_type) }}</v-chip>
                </template>
                <template v-slot:[`item.process`]="{ item }">
                    <strong class="op-cell-primary">{{ item.origin_process || 'N/A' }}</strong>
                    <small class="op-cell-secondary">{{ item.justice_branch || 'Ramo não informado' }}</small>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <v-chip small class="op-table-badge" :color="statusMeta(item.status).background" :text-color="statusMeta(item.status).color">
                        <i class="chc-queue__status-dot" :style="{ background: statusMeta(item.status).dot }" />{{ statusMeta(item.status).label }}
                    </v-chip>
                </template>
                <template v-slot:[`item.update`]="{ item }">
                    <span class="op-cell-primary">{{ item.updated_at | defaultDate("DD/MM/YYYY HH:mm") }}</span>
                    <small class="op-cell-secondary" :class="{ 'warning--text': [4, 6].includes(Number(item.status)) }">{{ statusMeta(item.status).description }}</small>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-btn small text class="op-row-action-button certificate-row-action" color="primary" elevation="0" @click="viewItem(item)">
                        <v-icon left small>{{ [0, 3, 5].includes(Number(item.status)) ? 'mdi-clipboard-text-search-outline' : 'mdi-eye-outline' }}</v-icon>
                        {{ [0, 3, 5].includes(Number(item.status)) ? 'Analisar' : 'Ver' }}
                    </v-btn>
                </template>
            </v-data-table>
        </template>
    </ManagementListPage>
</template>

<script>
import axios from "axios";
import creditorTypes from "@/common/data/creditor-types.data";
import certificateStatus from "@/common/data/credit-certificate-status.data";
import justiceBranchTypes from "@/common/data/justice-branch-types.data";
import creditOriginTypes from "@/common/data/credit-certificate-credit-origin-types.data";
import { cpf, cnpj } from "cpf-cnpj-validator";

export default {
    name: "CertificateCreditClaimList",
    data: () => ({
        breadcrumbs: [
            { text: "Início", to: "/home", exact: true },
            { text: "Certidão de Habilitação de Crédito", disabled: true },
        ],
        showAdvanced: false,
        dataset: {
            clients: [{ id: "", name: "Todos" }],
            creditorTypes,
            certificateStatus,
            justiceBranchTypes,
            creditOriginTypes,
        },
        summary: {},
        requesterTypes: [
            { id: "", name: "Todos" },
            { id: "user", name: "Credor" },
            { id: "lawyer", name: "Procurador" },
            { id: "debtor", name: "Devedora" },
        ],
        dataList: {
            filter: {
                protocol: "", term: "", status: "", client_id: "", requester_type: "",
                justice_branch: "", credit_origin: "", class_type: "", reviewed_class_type: "",
            },
            headers: [
                { text: "Protocolo", value: "protocol" },
                { text: "Requerente", value: "requester" },
                { text: "Tipo", value: "requester_type" },
                { text: "Processo", value: "process" },
                { text: "Status", value: "status" },
                { text: "Atualização / pendência", value: "update" },
                { text: "", value: "actions", align: "end", sortable: false },
            ],
            data: [], loading: false, count: 0,
            options: { page: 1, itemsPerPage: 10 },
            footerOptions: {
                itemsPerPageText: "Itens por página:",
                itemsPerPageOptions: [10, 20, 30, 50],
                pageText: "{0}-{1} de {2}",
            },
        },
    }),
    computed: {
        counters() {
            return [
                { key: 0, status: 0, label: "Pendentes", value: this.summary[0] || 0, tone: "neutral", active: String(this.dataList.filter.status) === "0" },
                { key: 3, status: 3, label: "Em análise", value: this.summary[3] || 0, tone: "info", active: String(this.dataList.filter.status) === "3" },
                { key: 4, status: 4, label: "Docs. solicitados", value: this.summary[4] || 0, tone: "warning", active: String(this.dataList.filter.status) === "4" },
                { key: 5, status: 5, label: "Docs. atualizados", value: this.summary[5] || 0, tone: "success", active: String(this.dataList.filter.status) === "5" },
                { key: 6, status: 6, label: "Desatualizadas", value: this.summary[6] || 0, tone: "purple", active: String(this.dataList.filter.status) === "6" },
            ];
        },
        nextPending() {
            return this.dataList.data.find((item) => [0, 5].includes(Number(item.status))) || null;
        },
        advancedFilterCount() {
            return ["protocol", "status", "client_id", "requester_type", "justice_branch", "credit_origin", "class_type", "reviewed_class_type"]
                .filter((key) => this.dataList.filter[key] !== "").length;
        },
        activeFilters() {
            const labels = {
                protocol: "Protocolo", status: "Status", client_id: "Caso", requester_type: "Requerente",
                justice_branch: "Ramo", credit_origin: "Origem",
                class_type: "Classe informada", reviewed_class_type: "Classe revisada",
            };
            return Object.keys(labels).filter((key) => this.dataList.filter[key] !== "")
                .map((key) => ({ key, label: `${labels[key]}: ${this.filterLabel(key)}` }));
        },
    },
    created() {
        this.load();
        this.loadClients();
    },
    methods: {
        pageChanged(page) {
            if (this.dataList.options.page === page) return;
            this.dataList.options.page = page;
            this.load();
        },
        pageSizeChanged(size) {
            if (this.dataList.options.itemsPerPage === size) return;
            this.dataList.options.itemsPerPage = size;
            this.dataList.options.page = 1;
            this.load();
        },
        applyFilters() {
            this.dataList.options.page = 1;
            this.load();
        },
        toggleStatus(status) {
            this.dataList.filter.status = String(this.dataList.filter.status) === String(status) ? "" : status;
            this.applyFilters();
        },
        removeFilter(key) {
            this.dataList.filter[key] = "";
            this.applyFilters();
        },
        clearFilters() {
            Object.keys(this.dataList.filter).forEach((key) => { this.dataList.filter[key] = ""; });
            this.showAdvanced = false;
            this.applyFilters();
        },
        clearAdvancedFilters() {
            ["protocol", "status", "client_id", "requester_type", "justice_branch", "credit_origin", "class_type", "reviewed_class_type"]
                .forEach((key) => { this.dataList.filter[key] = ""; });
            this.applyFilters();
        },
        filterLabel(key) {
            const value = this.dataList.filter[key];
            if (key === "status") return this.statusMeta(value).label;
            if (key === "client_id") return (this.dataset.clients.find((item) => item.id === value) || {}).name || value;
            if (key === "requester_type") return (this.requesterTypes.find((item) => item.id === value) || {}).name || value;
            const sources = {
                justice_branch: this.dataset.justiceBranchTypes,
                credit_origin: this.dataset.creditOriginTypes,
                class_type: this.dataset.creditorTypes,
                reviewed_class_type: this.dataset.creditorTypes,
            };
            return ((sources[key] || []).find((item) => item.id === value) || {}).name || value;
        },
        formatCpfCnpj(value) {
            if (!value) return "Documento não informado";
            const digits = String(value).replace(/\D/g, "");
            return digits.length === 14 ? cnpj.format(digits) : cpf.format(digits);
        },
        creditorName(item) {
            return item.creditors && item.creditors[0] ? item.creditors[0].name : "N/A";
        },
        creditorRegistration(item) {
            return item.creditors && item.creditors[0] ? item.creditors[0].registration : null;
        },
        getRequesterTypeLabel(type) {
            return { user: "Credor", lawyer: "Procurador", debtor: "Devedora" }[type] || "N/A";
        },
        getRequesterTypeColor(type) {
            return { user: "#E8ECFB", lawyer: "#E8F4F6", debtor: "#F3EFFB" }[type] || "#F2F4F7";
        },
        statusMeta(status) {
            return {
                0: { label: "Pendente", background: "#F2F4F7", color: "#475467", dot: "#98A2B3", description: "Aguardando início da análise" },
                1: { label: "Aprovada", background: "#E6F4EA", color: "#1E6B34", dot: "#1E6B34", description: "Análise concluída" },
                2: { label: "Rejeitada", background: "#FDECEC", color: "#A32020", dot: "#A32020", description: "Solicitação rejeitada" },
                3: { label: "Em análise", background: "#E8ECFB", color: "#2A3070", dot: "#2A3070", description: "Análise em andamento" },
                4: { label: "Documentos solicitados", background: "#FEF7EC", color: "#8A4B08", dot: "#C77A17", description: "Aguardando o requerente" },
                5: { label: "Documentos atualizados", background: "#E8F4F6", color: "#176B75", dot: "#238A96", description: "Novos documentos para conferir" },
                6: { label: "Certidão desatualizada", background: "#F3EFFB", color: "#5B3A9E", dot: "#7E55AB", description: "Aguardando nova certidão" },
            }[Number(status)] || { label: "Desconhecido", background: "#F2F4F7", color: "#475467", dot: "#98A2B3", description: "" };
        },
        getFilterCreditorTypes() {
            return [{ id: "", name: "Todos" }, ...this.dataset.creditorTypes];
        },
        getFilterJusticeBranchTypes() {
            return [{ id: "", name: "Todos" }, ...this.dataset.justiceBranchTypes];
        },
        getFilterCreditOriginTypes() {
            return [{ id: "", name: "Todos" }, ...this.dataset.creditOriginTypes];
        },
        getFilterStatusTypes() {
            return [{ id: "", name: "Todos" }, ...this.dataset.certificateStatus];
        },
        async load() {
            this.dataList.loading = true;
            const search = Object.fromEntries(Object.entries(this.dataList.filter)
                .filter(([, value]) => value !== "" && value !== null && value !== undefined));
            try {
                const response = await axios.get("/api/admin/users-creditors/credit-certificate-requests", {
                    params: {
                        page: this.dataList.options.page,
                        limit: this.dataList.options.itemsPerPage,
                        search: Object.keys(search).length ? search : null,
                    },
                });
                const content = response.data.content;
                this.dataList.data = content.data || [];
                this.dataList.count = content.paging.total;
                this.summary = content.summary || {};
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }
        },
        async loadClients() {
            try {
                const response = await axios.get("/api/clients", { params: { start: 1, limit: 100000, search: {} } });
                const clients = response.data.content.data || [];
                clients.sort((a, b) => a.name.localeCompare(b.name));
                this.dataset.clients = [{ id: "", name: "Todos" }, ...clients];
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
            }
        },
        viewItem(item) {
            sessionStorage.setItem("certificateCreditClaimContext", JSON.stringify({ id: item.id, mode: "view" }));
            this.$router.push("/certidao-credito-detalhes");
        },
        exportCsv() {
            const header = ["Protocolo", "Requerente", "CPF/CNPJ", "Tipo", "Processo", "Status", "Atualização"];
            const rows = this.dataList.data.map((item) => [
                item.protocol,
                item.requester_name || this.creditorName(item),
                item.requester_registration || this.creditorRegistration(item),
                this.getRequesterTypeLabel(item.requester_type),
                item.origin_process,
                this.statusMeta(item.status).label,
                item.updated_at,
            ]);
            const csv = [header, ...rows].map((row) => row.map((value) =>
                `"${String(value || "").replace(/"/g, '""')}"`).join(";")).join("\n");
            const url = URL.createObjectURL(new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" }));
            const link = document.createElement("a");
            link.href = url;
            link.download = "fila-analise-chc.csv";
            link.click();
            URL.revokeObjectURL(url);
        },
    },
};
</script>

<style scoped>
.chc-queue { max-width: 1480px; }
.chc-queue__header { display:flex; align-items:flex-end; justify-content:space-between; gap:24px; margin-bottom:20px; }
.chc-queue__header h1 { color:#101828; font-size:30px; line-height:1.2; }
.chc-queue__header p { max-width:680px; margin:6px 0 0; color:#667085; }
.chc-queue__header-actions { display:flex; gap:10px; }
.chc-queue__counters { display:grid; grid-template-columns:repeat(5,minmax(0,1fr)); gap:12px; margin-bottom:16px; }
.chc-queue__counters button { min-height:82px; padding:14px 16px; border:1px solid #E4E7EC; border-radius:9px; background:#fff; text-align:left; transition:.15s; }
.chc-queue__counters button:hover,.chc-queue__counters button.active { border-color:#2A3070; box-shadow:0 3px 12px rgba(42,48,112,.08); }
.chc-queue__counters span { display:flex; align-items:center; gap:7px; color:#667085; font-size:13px; font-weight:600; }
.chc-queue__counters i,.chc-queue__status-dot { display:inline-block; width:7px; height:7px; border-radius:50%; }
.chc-queue__counters strong { display:block; margin-top:7px; color:#101828; font-size:24px; }
.chc-queue__panel { overflow:hidden; border-color:#E4E7EC !important; border-radius:10px; }
.chc-queue__filters { display:grid; grid-template-columns:180px minmax(260px,1fr) 170px 170px auto; gap:10px; padding:14px; }
.chc-queue__advanced { display:grid; grid-template-columns:repeat(5,minmax(150px,1fr)); gap:10px; padding:14px; border-top:1px solid #E4E7EC; background:#FAFBFC; }
.chc-queue__advanced-actions { display:flex; grid-column:1/-1; justify-content:flex-end; gap:8px; }
.chc-queue__active-filters { display:flex; align-items:center; flex-wrap:wrap; gap:8px; padding:10px 14px; border-top:1px solid #E4E7EC; background:#F9FAFB; }
.chc-queue__active-filters small { margin-right:4px; color:#98A2B3; }
.chc-queue__active-filters button { color:#2A3070; font-size:12px; font-weight:600; text-decoration:underline; }
.chc-queue__table { border-top:1px solid #E4E7EC; }
.chc-queue__table strong,.chc-queue__table small,.chc-queue__table span { display:block; }
.chc-queue__table strong { color:#344054; font-size:13px; }
.chc-queue__table small { margin-top:3px; color:#667085; }
.chc-queue__process { font-family:monospace; font-weight:500 !important; }
.chc-queue__protocol { color:#2A3070 !important; font-family:monospace; letter-spacing:.03em; white-space:nowrap; }
.chc-queue__status-dot { margin-right:6px; vertical-align:middle; }
.certificate-claim-list ::v-deep .op-header-actions .v-btn {
    height:34px;
    padding:0 11px;
}
.certificate-claim-list ::v-deep .op-filter-toolbar { min-height:44px; }
.certificate-claim-list ::v-deep .op-filter-toolbar > .v-input { flex:0 1 560px; margin:0 !important; padding:0 !important; }
.certificate-claim-list ::v-deep .op-filter-toolbar .v-input__control,
.certificate-claim-list ::v-deep .op-filter-toolbar .v-input__slot { height:44px; margin:0 !important; }
.certificate-claim-list ::v-deep .op-filter-toolbar .v-text-field__slot { display:flex; align-items:center; height:42px; }
.certificate-claim-list ::v-deep .op-filter-toolbar .v-input__prepend-inner,
.certificate-claim-list ::v-deep .op-filter-toolbar .v-input__append-inner { align-self:center !important; margin-top:0 !important; }
.certificate-claim-list ::v-deep .op-filter-toolbar .v-input__prepend-inner .v-icon { font-size:18px !important; }
.certificate-claim-list ::v-deep .management-advanced-filters { margin:-5px; padding:0; }
.certificate-claim-list ::v-deep .management-advanced-filters .col { padding:5px; }
.certificate-claim-list ::v-deep .management-advanced-filters .op-field > label {
    display:block;
    margin-bottom:5px;
    color:#475569;
    font-size:9px;
    font-weight:600;
}
.certificate-claim-list ::v-deep .management-advanced-filters .v-input__slot { background:#fff !important; border-color:#d8e0e8 !important; }
.certificate-claim-list ::v-deep .certificate-protocol-field .v-input__prepend-inner { align-self:center !important; margin:0 8px 0 0 !important; }
.certificate-claim-list ::v-deep .certificate-protocol-field .v-input__prepend-inner .v-icon {
    display:inline-flex;
    align-items:center;
    justify-content:center;
    width:16px;
    height:16px;
    font-size:15px !important;
    line-height:16px !important;
}
.certificate-claim-list ::v-deep .certificate-filter-actions {
    justify-content:space-between;
    margin-top:4px;
}
.certificate-claim-list ::v-deep .certificate-filter-actions .v-btn {
    min-width:0;
    height:32px;
    padding:0 12px;
    border-radius:6px;
    font-size:10px;
    letter-spacing:0;
    text-transform:none;
}
.certificate-claim-list ::v-deep .certificate-row-action {
    min-width:0 !important;
    height:28px !important;
    padding:0 7px !important;
    font-size:9px !important;
    font-weight:600;
    letter-spacing:0 !important;
    text-transform:none !important;
}
.certificate-claim-list ::v-deep .certificate-row-action .v-icon { font-size:14px !important; }
@media (max-width: 1100px) {
    .chc-queue__counters { grid-template-columns:repeat(3,1fr); }
    .chc-queue__filters { grid-template-columns:1fr 1fr; }
    .chc-queue__advanced { grid-template-columns:repeat(2,1fr); }
}
@media (max-width: 700px) {
    .chc-queue__header { align-items:flex-start; flex-direction:column; }
    .chc-queue__header-actions { width:100%; flex-direction:column; }
    .chc-queue__counters { grid-template-columns:1fr 1fr; }
    .chc-queue__filters,.chc-queue__advanced { grid-template-columns:1fr; }
    .certificate-claim-list ::v-deep .op-filter-toolbar > .v-input { flex:1 1 auto; max-width:none; }
}
</style>
